
import router from '@/router';
import requestAPI from '@/logic/requestBeApi';
import UserRow from '@/components/UserRow.vue';
import EazyHeading from '@/components/EazyHeading.vue';
import FileUploader from '@/components/Inputs/FileUploader.vue';
import store from '@/store/index';
import languagesList from '@/resources/languagesList';
import newProjectHelper from './NewProjectHelper';
import CandidatesModal from './components/CandidatesModal.vue';
import BlueCheckbox from '@/components/BlueCheckbox.vue';
import CountryInput from '@/components/Inputs/CountryInput.vue';
import disableDates from '@/logic/helpers/dates/disableDates';
import inputValidators from '@/logic/helpers/inputs/validators';
import Favorite from '@/components/Favorite.vue';
import Modal from '@/components/Modals/Modal.vue';

export default {
  name: 'ClientNewProject',

  components: {
    UserRow,
    EazyHeading,
    FileUploader,
    CandidatesModal,
    BlueCheckbox,
    CountryInput,
    Favorite,
    Modal,
  },

  data(): Record<string, unknown> {
    return {
      router,
      disableDates,
      clientNewProject: {
        projectName: '',
        position: '',
        level: '',
        department: '',
        industry: '',
        language: '',
        tool: '',
        assessmentType: '',
        deadline: '',
        format: '',
        city: '',
        country: '',
        street: '',
        houseNumber: '',
        apartmentNumber: '',
        postalCode: '',
        videoAssessmentAllowed: false,
        pricePerAssessment: undefined,
        competenceFile: '',
        client: '',
      },
      newProjectValidationRules: {
        client: [{ validator: inputValidators.primitive, trigger: 'change' }],
        projectName: [{ validator: inputValidators.primitive, trigger: 'change' }],
        position: [{ validator: inputValidators.primitive, trigger: 'change' }],
        level: [{ validator: inputValidators.primitive, trigger: 'change' }],
        department: [{ validator: inputValidators.primitive, trigger: 'change' }],
        industry: [{ validator: inputValidators.primitive, trigger: 'change' }],
        language: [{ validator: inputValidators.primitive, trigger: 'change' }],
        assessmentType: [{ validator: inputValidators.assessmentType, trigger: 'change' }],
        format: [{ validator: inputValidators.primitive, trigger: 'change' }],
        deadline: [
          {
            validator: inputValidators.projectDeadline,
            trigger: inputValidators.projectDeadlineTriggerer(),
          },
        ],
        pricePerAssessment: [
          {
            validator: inputValidators.pricePerAssessment,
            trigger: inputValidators.pricePerAssessmentTriggerer(),
          },
        ],
      },
      invitationForAssessors: {
        invitationExpiresOn: '',
      },
      levels: [],
      departments: [],
      industries: [],
      tools: [],
      languages: [],
      assessmentTypes: [],
      expertiseTests: [],
      expertiseTestsForTool: [],
      toolsWithExpertiseTests: [],
      languagesList,
      fetchingCandidates: false,
      choosingCandidates: false,
      candidates: [],
      selectedCandidates: [],
      appendRecentlyAddedCandidatesToTheProject: false,
      recentlyAddedCandidatesNumber: this.getRecentlyAddedCandidatesNumber(),
      specifyingMatchingProperties: false,
      fetchingMatchingProperties: false,
      matchingProperties: '',
      clientPropertiesList: [
        { matching_property: '', position: 1 },
        { matching_property: '', position: 2 },
        { matching_property: '', position: 3 },
        { matching_property: '', position: 4 },
        { matching_property: '', position: 5 },
      ],
      processingProjectRequest: false,
      assessmentReqCreated: false,
      serverProcessingAssessors: false,
      assessors: [],
      assessorsFromFavs: [],
      selectedAssessors: [],
      notifyingAssessors: false,
      assessorsNotified: false,
      finallyNoAssessors: false,
      getAssessorsIterations: 0,
      userEditingProjectAfterNotFindingAssessors: false,
      projectId: this.$route.query.project || '',
      wholeScreenLoading: false,
      newCompetenceFileAssigned: false,
      openCandidatesModal: false,
      officeGetPending: false,
      assessmentsInMyOffice: false,
      expertiseTypesDelivered: false,
      formIsValid: false,
      noCandidateSelected: false,
      invalidLocation: false,
      chooseFromMatched: true,
      chooseFromFavs: false,
      showCostWarning: false,
      costWarningConfirmed: false,
      clients: [],
      isFreeClient: false,
      termsAccepted: false,
      regulationsAccepted: false,
      testsRemovalConfirmed: false,
      isConfirmationDialogOpen: false,
    };
  },

  computed: {
    legalsAccepted(): boolean {
      return this.termsAccepted;
    },

    isAssessorPlus(): boolean {
      return localStorage.getItem('roleType') === 'assessorPlus';
    },

    expertiseTestsFilteredByLanguageSelectedByUser(): string[] {
      const selectedLanguage = this.languages.find(
        (language) => language.id === this.clientNewProject.language,
      );
      if (!selectedLanguage) {
        return this.expertiseTestsForTool[this.clientNewProject.tool];
      }
      return this.expertiseTestsForTool[this.clientNewProject.tool].filter((expertiseTest) => {
        return selectedLanguage.slug.includes(expertiseTest.language);
      });
    },

    totalSum(): number {
      return this.expertiseTests.reduce((sum, testId) => sum + this.testPrices[testId], 0);
    },

    postedAssessment(): boolean {
      return this.assessmentReqCreated && !this.notifyingAssessors;
    },

    matchedAssessors(): boolean {
      return !this.serverProcessingAssessors && !this.assessorsNotified;
    },

    processingInvitations(): boolean {
      return this.notifyingAssessors && !this.assessorsNotified;
    },

    disabledProps(): string[] {
      return [this.prop1, this.prop2, this.prop3, this.prop4, this.prop5];
    },

    prop1(): string {
      return this.clientPropertiesList[0].matching_property;
    },

    prop2(): string {
      return this.clientPropertiesList[1].matching_property;
    },

    prop3(): string {
      return this.clientPropertiesList[2].matching_property;
    },

    prop4(): string {
      return this.clientPropertiesList[3].matching_property;
    },

    prop5(): string {
      return this.clientPropertiesList[4].matching_property;
    },

    allMatchingProps(): string[] {
      return [this.prop1, this.prop2, this.prop3, this.prop4, this.prop5];
    },

    atLeastOneMatchingPropChosen(): boolean {
      return !!(this.prop1 || this.prop2 || this.prop3 || this.prop4 || this.prop5);
    },

    comesBackFromCandidateCreation(): string {
      return this.$route.query.jump_to_candidates;
    },

    currentProject(): unknown {
      return store.state.singleProject;
    },

    minimalPricePerAssessment(): number {
      return store.state.meta.minimalPricePerAssessment;
    },

    price: {
      get(): number {
        const priceMultiplier = this.selectedCandidates.length || 1;

        return this.clientNewProject.pricePerAssessment * priceMultiplier;
      },

      set(newVal: number): number {
        return newVal;
      },
    },

    canGetProject(): boolean {
      const editableStatuses = ['draft', 'new', 'assessors_matched'];
      return editableStatuses.includes(this.currentProject.status) && this.expertiseTypesDelivered;
    },

    pricePerAssessmentHigherOrEqualToMinimal(): boolean {
      if (this.clientNewProject.pricePerAssessment === undefined) {
        return true;
      }

      return this.clientNewProject.pricePerAssessment >= this.minimalPricePerAssessment;
    },

    reportingFormats(): Record<string, unknown>[] {
      return [
        { desc: this.$t('newProjectReportingFormat1'), val: 'newProjectReportingFormat1' },
        { desc: this.$t('newProjectReportingFormat2'), val: 'newProjectReportingFormat2' },
        { desc: this.$t('newProjectReportingFormat3'), val: 'newProjectReportingFormat3' },
      ].sort((a, b) => a.desc.localeCompare(b.desc));
    },

    location(): Record<string, string> {
      return {
        city: this.clientNewProject.city,
        country: this.clientNewProject.country,
        street: this.clientNewProject.street,
        house_number: this.clientNewProject.houseNumber,
        apartment_number: this.clientNewProject.apartmentNumber,
        postal_code: this.clientNewProject.postalCode,
      };
    },

    expertises(): Record<string, string> {
      return {
        level: this.clientNewProject.level,
        department: this.clientNewProject.department,
        industry: this.clientNewProject.industry,
        language: this.clientNewProject.language,
        tool: this.clientNewProject.tool,
        assessmentType: this.clientNewProject.assessmentType,
      };
    },

    assessorsToChooseFrom(): Record<string, string>[] {
      let assessorsToChooseFrom = [];

      if (this.chooseFromMatched) {
        assessorsToChooseFrom = this.assessors;
      } else if (this.chooseFromFavs) {
        assessorsToChooseFrom = this.assessorsFromFavs;
      }

      return assessorsToChooseFrom;
    },
  },

  watch: {
    async openCandidatesModal(newVal: boolean): Promise<void> {
      if (newVal && this.isAssessorPlus) {
        const clientId = localStorage.getItem('clientIdFromProject');
        const url = `/api/v1/candidates?clients=${clientId}`;
        await this.getCandidates(url);
      }
    },

    expertiseTests: {
      handler(newVal, oldVal): void {
        if (newVal && newVal.length > 0 && !this.clientNewProject.language) {
          this.$message.error(this.$t('languageIsRequired'));
          this.expertiseTests = oldVal;
        }
      },
      deep: true,
    },

    canGetProject(): void {
      if (this.canGetProject) {
        setTimeout(() => {
          newProjectHelper.applyProjectFromStoreToUIModel(this, this.currentProject);
        }, 1);
      }
    },

    selectedCandidates(): void {
      this.recentlyAddedCandidatesNumber = this.getRecentlyAddedCandidatesNumber();
    },

    currentProject(): void {
      if (this.currentProject.status === 'pending') {
        this.$message.error(this.$t('accessForbidden'));
        router.push('/client-tasks');
      }
    },
  },

  async created(): Promise<void> {
    this.processingProjectRequest = true;
    store.commit('setSingleProject', {});
    this.getExpertiseTypes();
    this.getFavoriteAssessors();
    this.checkIfThisShouldGoToCandidateSelection();
    if (!this.isAssessorPlus) {
      await this.getCandidates();
    }
    if (this.isAssessorPlus) {
      await this.getClients();
    }
    newProjectHelper.getProject(this, this.projectId);
    await newProjectHelper.getMinimumPricePerAssessment(this);
    this.watchClientNewProjectTool();
    this.watchClientId();
    this.processingProjectRequest = false;
    this.checkIfIsFreeClient();
  },

  methods: {
    getTooltipContent(): void {
      if (this.isAssessorPlus) {
        return this.$t('assessorPlusProjectNameTooltip');
      } else {
        return this.$t('projectNameTooltip');
      }
    },

    clearSelectedExpertiseTest(): void {
      this.expertiseTests = null;
    },

    removeTest(testId: string): void {
      const index = this.expertiseTests.findIndex((id) => id === testId);
      if (index !== -1) {
        this.expertiseTests.splice(index, 1);
        if (this.expertiseTests.length === 0) {
          this.showCostWarning = false;
        }
      }
    },

    checkIfIsFreeClient(): void {
      if (localStorage.getItem('isFreeClient') === 'true') {
        this.isFreeClient = true;
      } else {
        return;
      }
    },

    saveBasics(buttonName: string): void {
      const selectedTests = this.expertiseTests && this.expertiseTests.length > 0;
      const submitProjectBasics = async (): Promise<void> => {
        if (selectedTests && !this.costWarningConfirmed && !this.isFreeClient) {
          await new Promise(() => {
            this.showAdditionalCostsModal(buttonName);
          });
        }
        if (this.costWarningConfirmed || !this.isFreeClient) {
          this.saveAndQuit = buttonName === 'saveAndQuit';
          newProjectHelper.submitProjectBasics(this, this.saveAndQuit);
        }
        if (this.isFreeClient) {
          this.saveAndQuit = buttonName === 'saveAndQuit';
          newProjectHelper.submitProjectBasics(this, this.saveAndQuit);
        }
      };

      submitProjectBasics();
    },

    submitProjectBasicsHelper(): void {
      if (this.saveAndQuit) {
        newProjectHelper.submitProjectBasics(this, true);
      } else {
        newProjectHelper.submitProjectBasics(this);
      }
    },

    acceptAdditionalCostsAndProceed(): void {
      if (!this.legalsAccepted || this.costWarningConfirmed) {
        return;
      } else {
        this.costWarningConfirmed = true;
        this.showCostWarning = false;
        this.submitProjectBasicsHelper();
      }
    },

    removeExpertiseTestsAndProceed(): void {
      this.isConfirmationDialogOpen = true;
    },

    confirmTestsRemoval(): void {
      this.expertiseTests = [];
      this.costWarningConfirmed = true;
      this.showCostWarning = false;
      this.submitProjectBasicsHelper();
      this.isConfirmationDialogOpen = false;
    },

    showAdditionalCostsModal(buttonName: string): void {
      const selectedTests = this.expertiseTests.length > 0;
      if (selectedTests) {
        this.showCostWarning = true;
        this.costWarningConfirmed = false;
      } else {
        this.costWarningConfirmed = true;
        this.showCostWarning = false;
      }
      this.buttonName = buttonName;
    },

    getToolNameFromUI(): string {
      const selectedTool = this.tools.find((tool) => tool.id === this.clientNewProject.tool);
      const nameRegex = /^(.*?)\s*\(/;
      const matches = selectedTool.name.match(nameRegex);
      return matches ? matches[1] : selectedTool.name;
    },

    watchClientNewProjectTool(): void {
      this.$watch(
        'clientNewProject.tool',
        (newVal: any) => {
          if (newVal) {
            this.clientNewProject.tool = newVal;
            this.getExpertiseTestsForTool(newVal);
          }
        },
        { immediate: true },
      );
    },

    watchClientId(): void {
      this.$watch(
        'clientNewProject.client',
        (newVal: any) => {
          if (newVal) {
            this.clientNewProject.client = newVal;
            if (this.isAssessorPlus) {
              const url = `/api/v1/candidates?clients=${newVal}`;
              this.getCandidates(url);
              localStorage.setItem('clientIdFromProject', newVal);
            } else {
              this.getCandidates();
            }
          }
        },
        { immediate: true },
      );
    },

    onToolChange(): void {
      this.getExpertiseTestsForTool(this.clientNewProject.tool);
      this.expertiseTests = [];
    },

    propSelectDisabled(i: number): boolean {
      return i > 0 && !this.clientPropertiesList[i - 1]?.matching_property;
    },

    handleFileUpload(payload: unknown): void {
      this.clientNewProject.competenceFile = payload;
      this.newCompetenceFileAssigned = true;
    },

    updateSelectedCandidates(candidateId: string): void {
      const candIdx = this.selectedCandidates.indexOf(candidateId);

      if (candIdx !== -1) this.selectedCandidates.splice(candIdx, 1);
      else this.selectedCandidates.push(candidateId);
    },

    updateSelectedAssessors(assessor: unknown): void {
      const assrIdx = this.selectedAssessors.indexOf(assessor);

      if (assrIdx !== -1) this.selectedAssessors.splice(assrIdx, 1);
      else this.selectedAssessors.push(assessor);
    },

    checkIfThisShouldGoToCandidateSelection(): void {
      if (this.comesBackFromCandidateCreation) {
        this.choosingCandidates = true;
        this.wholeScreenLoading = true;
        setTimeout(() => {
          this.wholeScreenLoading = false;
          this.openCandidatesModal = true;
        }, 2500);
      }
    },

    async getExpertiseTypes(): Promise<void> {
      const resp = await requestAPI({
        url: '/api/v1/expertise-types?order%5Bexpertises.name%5D=ASC',
        method: 'GET',
      });

      if (resp.ok) {
        const data = await resp.json();

        this.levelsList = data.find((obj) => obj.slug === 'levels').expertises;
        this.levels.push(...this.levelsList);

        this.functionsList = data.find((obj) => obj.slug === 'functions').expertises;
        this.departments.push(...this.functionsList);

        this.industriesList = data.find((obj) => obj.slug === 'industries').expertises;
        this.industries.push(...this.industriesList);

        this.toolsList = data.find((obj) => obj.slug === 'tools').expertises;
        this.tools.push(...this.toolsList);
        this.applyAdditionalSortingAndLabelingForTools();

        this.assessmentLanguagesList = data.find((obj) => obj.slug === 'languages').expertises;
        this.assessmentLanguagesList = this.assessmentLanguagesList.map(
          (assessmentLang: { name: string }) => {
            this.languagesList.forEach((lang: { code: string; name: string }) => {
              if (assessmentLang.name === lang.code) assessmentLang.name = lang.name;
            });

            return assessmentLang;
          },
        );
        this.languages.push(...this.assessmentLanguagesList);

        this.assessmentTypesList = data.find((obj) => obj.slug === 'assessment-types').expertises;
        this.assessmentTypes.push(...this.assessmentTypesList);

        this.expertiseTypesDelivered = true;
      }
    },

    applyAdditionalSortingAndLabelingForTools(): void {
      const toolsWithExpertiseTestsSet = new Set();
      this.tools.forEach((tool: { id: string; expertise_tests: any[] }) => {
        if (tool.expertise_tests && tool.expertise_tests.length > 0) {
          toolsWithExpertiseTestsSet.add(tool.id);
        }
      });
      this.toolsWithExpertiseTests = [...toolsWithExpertiseTestsSet];
      this.tools.sort((tool1, tool2) => {
        const hasTest1 = this.toolsWithExpertiseTests.includes(tool1.id);
        const hasTest2 = this.toolsWithExpertiseTests.includes(tool2.id);
        if (hasTest1 && !hasTest2) {
          return -1;
        } else if (!hasTest1 && hasTest2) {
          return 1;
        } else {
          return 0;
        }
      });
      this.tools = this.tools.map((tool) => {
        const hasTests = this.toolsWithExpertiseTests.includes(tool.id);
        return {
          id: tool.id,
          name: tool.name + (hasTests ? ` (${this.$t('testsAvailable')})` : ''),
        };
      });
    },

    async getExpertiseTestsForTool(toolId: string): Promise<void> {
      if (!toolId) {
        return;
      }
      const resp = await requestAPI({
        url: `/api/v1/expertises/${toolId}`,
        method: 'GET',
      });

      if (resp.ok) {
        const data = await resp.json();
        const expertiseTests = data.expertise_tests;
        if (data.expertise_tests.length > 0) {
          this.expertiseTestsForTool = expertiseTests;
          this.testPrices = {};
          this.testNames = {};
          expertiseTests.forEach((test) => {
            this.testPrices[test.id] = test.price;
            this.testNames[test.id] = test.name;
          });
          this.$set(this.expertiseTestsForTool, toolId, expertiseTests);
        } else {
          this.$set(this.expertiseTestsForTool, toolId, null);
        }
      }
    },

    async getCandidates(url?: string): Promise<void> {
      this.fetchingCandidates = true;
      const resp = await requestAPI({
        url: url || '/api/v1/candidates?per_page=100000',
        method: 'GET',
      });

      if (resp.ok) {
        const data = await resp.json();
        this.candidates = data;
        this.candidates = this.candidates.map((cand: { profile_picture: string }) => {
          if (cand.profile_picture === null) cand.profile_picture = '';
          return cand;
        });
      } else if (resp.status !== 401) this.$message.error(this.$t('failedToFetchSomeCandidates'));

      this.fetchingCandidates = false;
    },

    getExpertisesFromUI(): string[] {
      return Object.values(this.expertises)
        .map((expertise) => {
          return expertise ? `/api/v1/expertises/${expertise}` : undefined;
        })
        .filter((val) => {
          return val;
        });
    },

    getExpertiseTestsFromUI(): string[] {
      if (!this.expertiseTests || this.expertiseTests.length === 0) {
        return [];
      }
      return Object.values(this.expertiseTests)
        .map((expertiseTest) => {
          return expertiseTest ? `/api/v1/expertise-tests/${expertiseTest}` : undefined;
        })
        .filter((val) => {
          return val;
        });
    },

    getClientURIFromUI(): string {
      const clientURI = this.clientNewProject.client
        ? `/api/v1/clients/${this.clientNewProject.client}`
        : '';
      localStorage.setItem('clientURI', clientURI);
      return clientURI;
    },

    async getClients(): Promise<void> {
      const url = '/api/v1/favourites/clients';

      try {
        const resp = await requestAPI({ url, method: 'GET' });

        if (resp.ok) {
          const data = await resp.json();
          this.clients = [];

          for (const clientItem of data) {
            const clientResponse = await requestAPI({
              url: `/api/v1/clients/${clientItem.id}`,
              method: 'GET',
            });

            if (clientResponse.ok) {
              const client = await clientResponse.json();
              if (client.onboarded_fully === true) {
                const clientName = `${client.contact_details.first_name} ${client.contact_details.surname}`;
                this.clients.push({ id: clientItem.id, name: clientName });
              }
            } else {
              this.$message.error(this.$t('someErrorOccured'));
            }
          }
        } else if (resp.status !== 401) {
          this.$message.error(this.$t('someErrorOccured'));
        }
      } catch (error) {
        this.$message.error(this.$t('someErrorOccurred'));
      }
    },

    async saveMatchingPropsAndQuit(): Promise<void> {
      newProjectHelper.patchProjectWithMatchingProperties(this, true);
    },

    async saveMatchingProps(): Promise<void> {
      newProjectHelper.patchProjectWithMatchingProperties(this);
    },

    saveBasicsAndQuit(): void {
      newProjectHelper.submitProjectBasics(this, true);
    },

    validateAndProceedToCandidates(event: MouseEvent): void {
      event.preventDefault();
    },

    goToMatchingProps(): void {
      this.noCandidateSelected = !this.selectedCandidates.length;

      if (this.noCandidateSelected) return;

      this.choosingCandidates = false;

      newProjectHelper.patchProjectWithCandidates(this);

      this.getMatchingProperties();
      window.scrollTo(0, 0);
    },

    async saveCandidatesAndQuit(): Promise<void> {
      if (this.isAssessorPlus) {
        this.noCandidateSelected = !this.selectedCandidates.length;
        if (this.noCandidateSelected) return;
        await newProjectHelper.patchProjectWithCandidates(this, true);
        await newProjectHelper.patchProjectWithMatchingProperties(this, true);
        this.removeCandidatesFromLS();
      } else {
        this.choosingCandidates = false;
        const quit = true;
        await newProjectHelper.patchProjectWithCandidates(this, quit);
        this.removeCandidatesFromLS();
      }
    },

    removeCandidatesFromLS(): void {
      Object.keys(localStorage).forEach((key) => {
        const value = localStorage.getItem(key);

        if (value === 'recentlyAddedCand') {
          localStorage.removeItem(key);
        }
      });
    },

    async getMatchingProperties(): Promise<void> {
      this.specifyingMatchingProperties = true;
      this.fetchingMatchingProperties = true;

      const resp = await requestAPI({ url: '/api/v1/matching-properties', method: 'GET' });

      if (resp.ok) {
        const data = await resp.json();

        const sortedMatchingProperties = data.sort((a: { name: string }, b: { name: string }) => {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();

          if (aName > bName) return 1;
          if (aName < bName) return -1;
          return 0;
        });

        this.matchingProperties = sortedMatchingProperties;
      } else if (resp.status !== 401) {
        this.$message.error(this.$t('someErrorOccurred'));
      }

      this.fetchingMatchingProperties = false;
    },

    displayPropName(propId: string): string {
      if (!this.matchingProperties.length) return;

      const propObject = this.matchingProperties.find((prop: { id: any }) => prop.id === propId);

      return propObject ? propObject.name : '';
    },

    async getAssessors(iterations: number): Promise<void> {
      this.serverProcessingAssessors = true;
      this.getAssessorsIterations++;

      const resp = await requestAPI({
        url: `/api/v1/assessment-requests/${this.projectId}/assessors`,
        method: 'GET',
      });

      if (resp.status === 200) {
        this.assessors = await resp.json();
        this.assessors = this.assessors.sort(
          (a: { match: string }, b: { match: string }) => parseInt(b.match) - parseInt(a.match),
        );
        if (!this.assessors.length) this.finallyNoAssessors = true;
      } else if (resp.status === 202) {
        if (!this.assessors.length && iterations >= 10) {
          this.finallyNoAssessors = true;
          this.serverProcessingAssessors = false;
          return;
        }

        this.getAssessors(this.getAssessorsIterations);
        return;
      } else if (resp.status !== 401) this.$message.error(this.$t('someErrorOccurred'));

      this.serverProcessingAssessors = false;
    },

    async notifyAssessors(): Promise<void> {
      newProjectHelper.patchProjectWithAssessors(this);
    },

    takeClientToTheHomeScreen(): void {
      store.commit('applyAssessmentsFilter', { mode: 'projects', filter: 'requests' });
      store.commit('setRecentlyEditedProjectId', this.projectId);

      router.push('client-tasks');
    },

    getCandidateUrl(profPic: { url: any }): string | undefined {
      return profPic ? profPic.url : undefined;
    },

    isCandidateSelected(candidateId: string): boolean {
      return this.selectedCandidates.includes(candidateId);
    },

    isAssessorSelected(assessorId: string): boolean {
      return this.selectedAssessors.includes(assessorId);
    },

    ifItIsNotLastProp(i: number): boolean {
      return !!this[`prop${i + 2}`];
    },

    deleteMatchingProp(i: number): void {
      this.clientPropertiesList[i].matching_property = '';
    },

    discretlyGoBackToTheFirstStage(): void {
      this.userEditingProjectAfterNotFindingAssessors = true;
      this.assessmentReqCreated = false;
      this.choosingCandidates = false;
      this.specifyingMatchingProperties = false;
      this.finallyNoAssessors = false;
      this.getAssessorsIterations = false;
    },

    getMyScope(): unknown {
      return this;
    },

    isDeadlineExpired(): boolean {
      return new Date(this.deadline).getTime() < new Date().getTime();
    },

    async getMyOfficeLocation(): Promise<void> {
      this.officeGetPending = true;
      const url = this.isAssessorPlus
        ? '/api/v1/clients/' + this.clientNewProject.client
        : '/api/v1/clients/me';

      const resp = await requestAPI({
        method: 'GET',
        url,
      });

      if (resp.ok) {
        const data = await resp.json();

        this.clientNewProject.city = data.company.addresses[0].city;
        this.clientNewProject.country = data.company.addresses[0].country;
        this.clientNewProject.street = data.company.addresses[0].street;
        this.clientNewProject.houseNumber = data.company.addresses[0].house_number;
        this.clientNewProject.apartmentNumber = data.company.addresses[0].apartment_number;
        this.clientNewProject.postalCode = data.company.addresses[0].postal_code;
        this.locationId = data.company.addresses[0].id;
      }

      this.officeGetPending = false;
    },

    clearLocationFromMyOffice(): void {
      this.clientNewProject.city = '';
      this.clientNewProject.country = '';
      this.clientNewProject.street = '';
      this.clientNewProject.houseNumber = '';
      this.clientNewProject.apartmentNumber = '';
      this.clientNewProject.postalCode = '';
    },

    getOrWipeMyOffice(): void {
      if (this.assessmentsInMyOffice) {
        if (this.isAssessorPlus && this.clientNewProject.client === '') {
          this.$message.error(this.$t('selectingClientRequired'));
          return;
        }
        this.getMyOfficeLocation();
      } else {
        this.clearLocationFromMyOffice();
      }
    },

    getRecentlyAddedCandidatesNumber(): number {
      const recentlyAddedCandidatesIds = Object.keys(localStorage).filter((candidateId) => {
        return (
          localStorage[candidateId] === 'recentlyAddedCand' &&
          !this.selectedCandidates?.includes(candidateId)
        );
      });

      return recentlyAddedCandidatesIds.length;
    },

    leaveNewProjectScreen(): void {
      store.commit('applyAssessmentsFilter', { mode: 'projects', filter: 'requests' });
      store.commit('setReloadSpecificList', 'reports');
      store.commit('setRecentlyEditedProjectId', this.projectId);

      setTimeout(() => {
        router.push('/client-tasks');
      }, 100);
    },

    showMatchedAssessors(): void {
      this.chooseFromMatched = true;
      this.chooseFromFavs = false;
    },

    showFavouriteAssessors(): void {
      this.chooseFromMatched = false;
      this.chooseFromFavs = true;
    },

    async getFavoriteAssessors(): Promise<void> {
      let url = '/api/v1/favourites/assessors';
      const resp = await requestAPI({ url, method: 'GET' });

      if (resp.ok) {
        const data = await resp.json();
        this.assessorsFromFavs = data.map((assessor) => {
          return {
            ...assessor,
            profile_image: undefined,
            first_name: assessor.contact_details.first_name,
            surname: assessor.contact_details.surname,
          };
        });
      } else if (resp.status !== 401) {
        this.$message.error(this.$t('someErrorOccured'));
      }
    },
  },
};
